// ProjectRow: una fila horizontal con 2 cells alineadas globalmente
// (ISSUE | PRs). Ya NO hay celda de proyecto por fila (repetía el nombre del
// repo en cada línea); la identidad del repo vive en el header de la sección +
// el borde izquierdo de color de cada fila. Cronograma visual del thread: el
// ISSUE es el más descriptivo y los PRs/merges aparecen como pills compactas
// (#N). La pill del merge principal lleva adosado el checkbox de reportado +
// su age. Nada desaparece al avanzar de estado — la TV es pizarra de historial.
//
// Densidad TV 4K: filas ultra-compactas de altura uniforme. El título del
// ISSUE se ve COMPLETO (sin ellipsis): FitText encoge la fuente hasta que el
// texto entra en una sola línea (mín. 5px), manteniendo la fila igual de alta.
//
// Estados visuales del row (via data-state):
// - "ready-to-report": hay merge sin label 'reported' → highlight emerald sutil
//   (señal "ya listo, falta tickear"). Aplica también cuando el issue padre
//   está cerrado (caso típico post-merge).
// - "reported": el merge tiene label 'reported' → opacity-50 + tachado.
// - sin data-state: thread normal (issue abierto sin merge).

import { useLayoutEffect, useRef } from 'react'
import { CheckCircle2, XCircle, Clock, AlertTriangle, CircleDot, GitPullRequest, GitMerge, Square, CheckSquare } from 'lucide-react'
import type { Issue, PR, MergedPR, Run } from '../github'
import type { Thread } from '../threads'
import type { Repo } from '../repos'
import { getSeverity, isReported, useToggleReported, ageInDays, relativeTime, localTimestamp, type Severity } from '../queries'
import { redactRut } from '../phi'
import { TypeBadge } from './TypeBadge'

const SEV_COLORS: Record<Severity, string> = {
  p0: 'bg-red-500 text-white',
  p1: 'bg-amber-500 text-zinc-950',
  p2: 'bg-zinc-600 text-zinc-100',
  p3: 'bg-zinc-700 text-zinc-300',
  untriaged: 'bg-zinc-800 text-zinc-500',
}

const REPO_BORDER: Record<string, string> = {
  'wipxap/wipxap-fitlab':     'border-red-400',
  'wipxap/CEMSA':             'border-blue-400',
  'wipxap/kunamasta':         'border-fuchsia-400',
  'wipxap/TITANIUM':          'border-cyan-400',
  'wipxap/cea-by-incluyeme':  'border-emerald-400',
  'wipxap/ict-spa':           'border-orange-400',
  'wipxap/cemsa-activa':      'border-teal-500',
}

interface ProjectRowProps {
  thread: Thread
  repo: Repo
  runs: Run[]
}

export function ProjectRow({ thread, repo, runs }: ProjectRowProps) {
  // El merge "principal" es el más reciente (merges[0]). La cell PRs muestra UNA
  // pill por CADA merge; la del principal lleva adosado el checkbox de reportado
  // + su age (ya no hay 4ª celda MERGE separada). Un solo useToggleReported por
  // fila, al top — rules-of-hooks: jamás dentro del .map de las pills.
  const principalMerge = thread.merges[0]
  const reported = principalMerge ? isReported(principalMerge) : false
  const toggle = useToggleReported(repo.slug)
  const rowState: 'ready-to-report' | 'reported' | undefined =
    principalMerge ? (reported ? 'reported' : 'ready-to-report') : undefined

  const rowStateClass =
    rowState === 'ready-to-report'
      ? 'bg-emerald-500/[0.06] hover:bg-emerald-500/[0.09]'
      : rowState === 'reported'
        ? 'opacity-50'
        : 'hover:bg-zinc-900/40'

  const borderClass = REPO_BORDER[repo.slug] ?? 'border-zinc-700'

  return (
    <div
      data-testid={`thread-${thread.key}`}
      data-state={rowState}
      className={`grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-2 4k:gap-4 items-center min-h-[2.25rem] 4k:min-h-[3rem] px-2 4k:px-4 py-0.5 4k:py-1 border-b border-zinc-900/60 border-l-2 ${borderClass} transition-colors ${rowStateClass}`}
    >
      <IssueCell issue={thread.issue} />
      <PRsCell
        prs={thread.prs}
        merges={thread.merges}
        runs={runs}
        threadKey={thread.key}
        principalMergeNumber={principalMerge?.number}
        reported={reported}
        togglePending={toggle.isPending}
        onToggleReported={() => principalMerge && toggle.mutate({ prNumber: principalMerge.number, reported })}
      />
    </div>
  )
}

// FitText: muestra el título COMPLETO en HASTA 2 líneas (sin ellipsis),
// encogiendo la fuente solo si hace falta para que quepa. Mide el ancho natural
// del texto en una línea (nowrap) y lo compara contra la capacidad de 2 líneas
// (2 × ancho disponible, con margen para el wrapping por palabras). `line-clamp-2`
// es la red de seguridad. Re-mide con ResizeObserver cuando la celda cambia de
// ancho (rotación / viewport). No usa estado → no re-renderiza.
function FitText({ text, title, className, testId }: { text: string; title: string; className?: string; testId?: string }) {
  const ref = useRef<HTMLSpanElement>(null)
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    let lastW = -1
    const fit = () => {
      el.style.fontSize = '' // volver al tamaño base (text-xs / 4k:text-base)
      const base = parseFloat(getComputedStyle(el).fontSize) || 12
      const avail = el.clientWidth
      if (avail <= 0) return
      // Ancho natural en UNA línea: forzar block+nowrap temporalmente para medir.
      const prevD = el.style.display
      const prevW = el.style.whiteSpace
      el.style.display = 'block'
      el.style.whiteSpace = 'nowrap'
      const needed = el.scrollWidth
      el.style.display = prevD
      el.style.whiteSpace = prevW
      // Capacidad de 2 líneas (0.9 de margen por líneas ragged al partir palabras).
      const capacity = avail * 2 * 0.9
      if (needed > capacity) {
        const fs = Math.max(5, Math.floor(base * (capacity / needed) * 10) / 10)
        el.style.fontSize = `${fs}px`
      }
    }
    fit()
    const ro = new ResizeObserver((entries) => {
      const w = entries[0].contentRect.width
      if (Math.abs(w - lastW) < 1) return // solo re-mide en cambios de ancho (evita loop por alto)
      lastW = w
      fit()
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [text])
  return (
    <span
      ref={ref}
      data-testid={testId}
      title={title}
      className={`flex-1 min-w-0 leading-tight line-clamp-2 ${className ?? ''}`}
    >
      {text}
    </span>
  )
}

function IssueCell({ issue }: { issue?: Issue }) {
  if (!issue) return <div />

  const sev = getSeverity(issue)
  const closed = issue.state === 'closed'
  const StatusIcon = closed ? CheckCircle2 : CircleDot
  const statusColor = closed ? 'text-emerald-400' : 'text-red-400'
  const titleClass = closed ? 'text-zinc-400' : 'text-zinc-200'

  return (
    <div
      className="flex items-center gap-1.5 4k:gap-2 min-w-0"
      data-testid={`thread-issue-cell-${issue.number}`}
      data-issue-state={issue.state}
    >
      <StatusIcon
        className={`h-3.5 w-3.5 4k:h-4 4k:w-4 ${statusColor} shrink-0`}
        data-testid={`issue-status-icon-${issue.number}`}
        data-state={issue.state}
      />
      <span
        className={`px-1 py-0 rounded text-[10px] 4k:text-xs font-bold leading-tight shrink-0 ${SEV_COLORS[sev]}`}
        data-testid={`sev-badge-${sev}`}
      >
        {sev === 'untriaged' ? '?' : sev.toUpperCase()}
      </span>
      <TypeBadge issue={issue} />
      <a
        href={issue.html_url}
        target="_blank"
        rel="noreferrer"
        className="font-mono font-bold text-zinc-200 hover:text-zinc-50 shrink-0 text-xs 4k:text-base"
        data-testid={`issue-folio-${issue.number}`}
      >
        #{issue.number}
      </a>
      <FitText
        text={redactRut(issue.title)}
        title={issue.title}
        testId={`issue-title-${issue.number}`}
        className={`text-xs 4k:text-base ${titleClass}`}
      />
      <span
        className="shrink-0 text-[10px] 4k:text-xs text-zinc-500 font-mono tabular-nums"
        title={`Creado: ${localTimestamp(issue.created_at)}`}
        data-testid={`issue-age-${issue.number}`}
      >
        {relativeTime(issue.created_at)}
      </span>
    </div>
  )
}

interface PRsCellProps {
  prs: PR[]
  merges: MergedPR[]
  runs: Run[]
  threadKey: string
  // El merge principal (más reciente) lleva el checkbox de reportado + su age
  // adosados a su pill; el resto de merge-pills quedan planas.
  principalMergeNumber?: number
  reported: boolean
  togglePending: boolean
  onToggleReported: () => void
}

// Cell PRs (cronograma): una pill por cada PR del thread y una por cada
// merge. PRs ordenados por created_at ascendente; merges ordenados por
// merged_at descendente — el más reciente cierra la línea de tiempo.
// Cada pill: icono + #N + tooltip con título completo. Sin texto del título
// visible — el lenguaje del thread vive en la cell ISSUE; aquí solo importa
// "qué intentos hubo y cómo terminaron".
function PRsCell({
  prs,
  merges,
  runs,
  threadKey,
  principalMergeNumber,
  reported,
  togglePending,
  onToggleReported,
}: PRsCellProps) {
  if (prs.length === 0 && merges.length === 0) {
    return <div data-testid={`thread-prs-cell-${threadKey}`} />
  }

  // Merges en orden cronológico (más viejo → más reciente) para leerlos como
  // timeline. El thread.merges viene ordenado descendente, lo invertimos.
  const mergesChrono = [...merges].reverse()

  return (
    <div
      className="flex flex-wrap items-center gap-1 4k:gap-1.5 min-w-0"
      data-testid={`thread-prs-cell-${threadKey}`}
    >
      {prs.map((pr) => (
        <PRPill key={pr.number} pr={pr} runs={runs} />
      ))}
      {mergesChrono.map((m) => (
        <MergePill
          key={m.number}
          merge={m}
          isPrincipal={m.number === principalMergeNumber}
          reported={reported}
          togglePending={togglePending}
          onToggleReported={onToggleReported}
        />
      ))}
    </div>
  )
}

function PRPill({ pr, runs }: { pr: PR; runs: Run[] }) {
  // GitHub no devuelve `closed_at` en el list endpoint de open PRs (siempre
  // null). Para los closed-unmerged que vienen de fetchClosedUnmergedPRs el
  // closed_at sí está y diferenciamos por la presencia del campo. Como PR
  // base no incluye closed_at en su tipo, lo leemos con cast defensivo.
  const closedAt = (pr as PR & { closed_at?: string | null }).closed_at ?? null
  const isClosed = !!closedAt
  const state: 'open' | 'closed' = isClosed ? 'closed' : 'open'
  const ciRun = runs.find((r) => r.head_sha === pr.head.sha)
  const ageDays = ageInDays(pr.created_at)
  const stale = state === 'open' && ageDays > 7

  const Icon = isClosed ? XCircle : GitPullRequest
  const pillClass = isClosed
    ? 'bg-red-500/10 border-red-500/30 text-red-300 hover:bg-red-500/20'
    : stale
      ? 'bg-amber-500/10 border-amber-500/30 text-amber-300 hover:bg-amber-500/20'
      : 'bg-blue-500/10 border-blue-500/30 text-blue-300 hover:bg-blue-500/20'

  const refIso = isClosed && closedAt ? closedAt : pr.created_at
  const refLabel = isClosed ? 'Cerrado' : 'Creado'

  return (
    <a
      href={pr.html_url}
      target="_blank"
      rel="noreferrer"
      data-testid={`pr-pill-${pr.number}`}
      data-state={state}
      className={`inline-flex items-center gap-1 px-1.5 py-0 rounded border text-[10px] 4k:text-xs font-mono font-bold transition-colors shrink-0 ${pillClass}`}
      title={`${pr.title}\n${refLabel}: ${localTimestamp(refIso)}`}
    >
      <Icon className="h-3 w-3 4k:h-3.5 4k:w-3.5 shrink-0" />
      <span>#{pr.number}</span>
      {ciRun && !isClosed && <CiIcon run={ciRun} />}
    </a>
  )
}

// La merge pill cierra el timeline a la derecha de los PR pills. La del merge
// PRINCIPAL (más reciente) absorbe la única interacción del board: el checkbox
// de reportado + el age, que antes vivían en la 4ª celda MERGE (ya eliminada).
function MergePill({
  merge,
  isPrincipal,
  reported,
  togglePending,
  onToggleReported,
}: {
  merge: MergedPR
  isPrincipal: boolean
  reported: boolean
  togglePending: boolean
  onToggleReported: () => void
}) {
  return (
    <span
      data-testid={`pr-pill-${merge.number}`}
      data-state="merged"
      className={`inline-flex items-center gap-1 px-1.5 py-0 rounded border bg-purple-500/10 border-purple-500/30 text-purple-300 text-[10px] 4k:text-xs font-mono font-bold shrink-0 ${reported && isPrincipal ? 'opacity-70' : ''}`}
      title={merge.merged_at ? `Mergeado: ${localTimestamp(merge.merged_at)}` : merge.title}
    >
      {isPrincipal && (
        <button
          type="button"
          onClick={onToggleReported}
          disabled={togglePending}
          aria-label={reported ? `Desmarcar reporte #${merge.number}` : `Marcar #${merge.number} como reportado`}
          data-testid={`reported-checkbox-${merge.number}`}
          data-state={reported ? 'reported' : 'pending'}
          className="shrink-0 text-zinc-400 hover:text-emerald-400 disabled:opacity-50 transition-colors"
        >
          {reported ? (
            <CheckSquare className="h-3 w-3 4k:h-3.5 4k:w-3.5 text-emerald-500" />
          ) : (
            <Square className="h-3 w-3 4k:h-3.5 4k:w-3.5" />
          )}
        </button>
      )}
      <GitMerge className="h-3 w-3 4k:h-3.5 4k:w-3.5 shrink-0" />
      <a
        href={merge.html_url}
        target="_blank"
        rel="noreferrer"
        className={`hover:text-purple-100 ${reported && isPrincipal ? 'line-through' : ''}`}
      >
        #{merge.number}
      </a>
      {isPrincipal && merge.merged_at && (
        <span
          data-testid={`merge-age-${merge.number}`}
          className="text-zinc-400 tabular-nums"
          title={`Mergeado: ${localTimestamp(merge.merged_at)}`}
        >
          {relativeTime(merge.merged_at)}
        </span>
      )}
    </span>
  )
}

function CiIcon({ run }: { run: Run | undefined }) {
  if (!run) return <Clock className="h-3 w-3 4k:h-4 4k:w-4 text-zinc-600 shrink-0" />
  if (run.status !== 'completed')
    return <Clock className="h-3 w-3 4k:h-4 4k:w-4 text-amber-500 shrink-0" />
  if (run.conclusion === 'success')
    return <CheckCircle2 className="h-3 w-3 4k:h-4 4k:w-4 text-green-500 shrink-0" />
  if (run.conclusion === 'failure')
    return <XCircle className="h-3 w-3 4k:h-4 4k:w-4 text-red-500 shrink-0" />
  return <AlertTriangle className="h-3 w-3 4k:h-4 4k:w-4 text-zinc-500 shrink-0" />
}
